import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '@contexts/LanguageContext';
import { getTranslation } from '@utils/i18n';

const StyledJobsSection = styled.section`
  max-width: 700px;

  .jobs-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    margin: 0;
    list-style: none;
  }
`;

const StyledJobItem = styled.li`
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  border-bottom: 1px solid var(--lightest-navy);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  ul {
    ${({ theme }) => theme.mixins.fancyList};
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: var(--fz-xl);
    font-weight: 500;
    line-height: 1.3;

    .company {
      color: var(--green);
    }

    a.inline-link {
      border: none;
    }
  }

  .range {
    margin: 0 0 16px 0;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .job-description {
    font-size: var(--fz-md);
    line-height: 1.6;
    color: var(--light-slate);

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  @media (max-width: 768px) {
    padding: 20px 0;

    h3 {
      font-size: var(--fz-lg);
      margin-bottom: 6px;
    }

    .range {
      margin-bottom: 12px;
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
              lang
            }
            html
          }
        }
      }
    }
  `);

  const { language } = useLanguage();
  const jobsData = data.jobs.edges.filter(
    edge => (edge.node.frontmatter.lang || 'en') === language,
  );
  const revealContainer = useRef(null);
  const revealItems = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
    revealItems.current.forEach((ref, i) => ref && sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">{getTranslation(language, 'jobs.title')}</h2>

      <ul className="jobs-list" aria-label="Experience list">
        {jobsData &&
          jobsData.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { title, url, company, range } = frontmatter;

            return (
              <StyledJobItem key={i} ref={el => (revealItems.current[i] = el)}>
                <h3>
                  <span>{title}</span>
                  <span className="company">
                    &nbsp;@&nbsp;
                    {url ? (
                      <a
                        href={url}
                        className="inline-link"
                        target="_blank"
                        rel="noopener noreferrer">
                        {company}
                      </a>
                    ) : (
                      <span>{company}</span>
                    )}
                  </span>
                </h3>

                <p className="range">{range}</p>

                <div className="job-description" dangerouslySetInnerHTML={{ __html: html }} />
              </StyledJobItem>
            );
          })}
      </ul>
    </StyledJobsSection>
  );
};

export default Jobs;
